import React, { useEffect, useState } from 'react';
import { Box, Typography, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Chip, Divider, Grid, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import cotizacionVentaService, { CotizacionVenta, CotizacionVentaDetalle } from '../../services/cotizacionVentaService';
import api from '../../services/api';
import clienteService, { OPTIONS_CLIENTE_OPERATIVO } from '../../services/clienteService';
import ubicacionService from '../../services/ubicacionService';
import FacturaPOSClientePanel from '../facturaventa/pos/FacturaPOSClientePanel';
import FacturaPOSSearchBar, { SuggestionItem } from '../facturaventa/pos/FacturaPOSSearchBar';
import FacturaPOSLineasGrid from '../facturaventa/pos/FacturaPOSLineasGrid';
import FacturaPOSTotalesPanel from '../facturaventa/pos/FacturaPOSTotalesPanel';
import { POSCliente, POSLinea } from '../facturaventa/pos/types';
import { obtenerCodigoListaCliente } from '../../utils/precioListaPedido';
import { parseLocalDate, toLocalDateStringYMD } from '../../utils/formatters';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  calcularFechaVencimDesdeVtoComprob,
  sameCalendarDay,
  toLocalDateInputValue,
  validarFechasFacturaVenta,
} from '../../utils/facturaVentaFechas';
import { mapearVencimComprobanteCargado } from '../../utils/facturaVentaVencim';
import { resolveVtoComprobCliente } from '../../utils/vtoComprobCliente';

type CotizacionVentaUI = CotizacionVenta & { ordenCompra?: string; transporte?: string; ubicacion?: string; formaDePago?: string };
type CotizacionVentaUIExt = CotizacionVentaUI & { nroSuc?: number; cuit?: string; tipoIVA?: string; dni?: string; idCompania?: string };

interface SucursalVenta {
  nroSuc: number;
  nombreSucursal?: string;
}

const FORMAS_DE_PAGO_VALIDAS = ['CONTADO', 'CTA.CTE.', 'EFECTIVO', 'DEVOLUCION', 'CONTRAREEMBOLSO'];
/** Fallback al cargar cotización existente sin sucursal en el GET. */
const DEFAULT_NRO_SUC = 1;
/** Cotización nueva: 0 hasta que el combo tome la primera sucursal de la lista cargada. */
const NRO_SUC_COTIZACION_NUEVA = 0;

const obtenerTransporteSugerido = (cliente?: { nroExpreso?: number | string | null } | null): string => {
  if (!cliente) return '';
  const rawValue = cliente.nroExpreso ?? (cliente as any)?.nroexpreso ?? null;
  if (rawValue === undefined || rawValue === null) return '';
  const strValue = String(rawValue).trim();
  if (!strValue || strValue === '0') return '';
  return strValue;
};

const normalizarFormaDePago = (valor?: string | null): string => {
  if (!valor) return '';
  const cleaned = String(valor).trim().toUpperCase();
  const match = FORMAS_DE_PAGO_VALIDAS.find(forma => forma.toUpperCase() === cleaned);
  return match || '';
};

const obtenerFormaPagoSugerida = (cliente?: { condVta?: string | null } | null): string => {
  if (!cliente) return 'EFECTIVO';
  return normalizarFormaDePago(cliente.condVta ?? (cliente as any)?.condvta ?? null);
};

const normalizarPorcentajeBonificacion = (valor?: number | string | null): number => {
  if (valor === null || valor === undefined) return 0;
  const numero = Number(valor);
  return Number.isFinite(numero) ? Math.max(0, Math.min(100, numero)) : 0;
};

const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const calcularSubtotal = (l: Pick<POSLinea, 'cantidad' | 'precioUnitario' | 'descuento'>): number => {
  const base = l.cantidad * l.precioUnitario;
  return Math.round(base * (1 - l.descuento / 100) * 100) / 100;
};

const CotizacionesVenta: React.FC = () => {
  const navigate = useNavigate();
  const { cotizacionId } = useParams<{ cotizacionId?: string }>();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [loadingCotizacion, setLoadingCotizacion] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [posCliente, setPosCliente] = useState<POSCliente | null>(null);
  const [lineasPOS, setLineasPOS] = useState<POSLinea[]>([]);
  const [sucursales, setSucursales] = useState<SucursalVenta[]>([]);
  const [loadingSucursales, setLoadingSucursales] = useState(false);

  const modo = cotizacionId ? (location.pathname.includes('/ver') ? 'ver' : 'editar') : 'crear';
  const [cotizacionVenta, setCotizacionVenta] = useState<CotizacionVentaUIExt>({
    nroFormulario: 0,
    nroTipoFormulario: '10',
    tipoCliente: 'C',
    fechaEmision: new Date(),
    fechaVencim: new Date(),
    horaEmision: new Date(),
    nroCliente: 0,
    nombre: '',
    nroVendedor: 0,
    nroLista: '',
    codigoMoneda: 'ARS',
    importeTotal: 0,
    asentado: 'N',
    descripcion: '',
    usuario: '',
    ordenCompra: '',
    transporte: '',
    ubicacion: '',
    formaDePago: '',
    nroSuc: NRO_SUC_COTIZACION_NUEVA,
    cuit: '',
    tipoIVA: '',
    idCompania: '',
    detalles: [],
    porcDesc: 0,
    porcDesc2: 0,
    porcDesc3: 0,
  });

  const isEditMode = modo === 'editar';
  /** Hay ID en la URL (editar/ver): no sobrescribir cabecera con defaults del cliente (p. ej. vendedor). */
  const esCotizacionPersistente = Boolean(cotizacionId);
  const isReadOnlyByPath = modo === 'ver';
  const isReadOnlyByEstado = (cotizacionVenta.estado === 'Facturado' || cotizacionVenta.estado === 'Preparado') && isEditMode;
  const isReadOnly = isReadOnlyByPath || isReadOnlyByEstado;

  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [nroCotizacionCreada, setNroCotizacionCreada] = useState<number | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const round2 = (n: number) => Math.round(n * 100) / 100;

  useEffect(() => {
    if (cotizacionId) {
      const cargarCotizacion = async () => {
        try {
          setLoadingCotizacion(true);
          setError(null);
          const id = parseInt(cotizacionId, 10);
          if (isNaN(id)) {
            setError('ID de cotizacion invalido');
            navigate('/ventas/cotizaciones');
            return;
          }

          const cotizacionCargada = await cotizacionVentaService.getCotizacionVenta(id);
          let ubicacionFormateada = (cotizacionCargada as any).ubicacion || '';
          const nroUbicacionOrigen = (cotizacionCargada as any).nroUbicacionOrigen || (cotizacionCargada as any).nroUbicacion;

          if (!ubicacionFormateada && nroUbicacionOrigen) {
            try {
              const ubicaciones = await ubicacionService.getUbicaciones();
              const ubicacionEncontrada = ubicaciones.find(u => u.nroUbicacion === nroUbicacionOrigen);
              ubicacionFormateada = ubicacionEncontrada
                ? `${ubicacionEncontrada.nombreUbicacion} (${ubicacionEncontrada.nroUbicacion})`
                : `(${nroUbicacionOrigen})`;
            } catch {
              ubicacionFormateada = nroUbicacionOrigen?.toString() || '';
            }
          }

          let transporteValue = (cotizacionCargada as any).transporte || '';
          const nroExpreso = (cotizacionCargada as any).nroExpreso;
          if (!transporteValue && nroExpreso !== undefined && nroExpreso !== null && nroExpreso !== 0 && nroExpreso !== '') {
            transporteValue = String(nroExpreso);
          } else if (transporteValue) {
            const match = String(transporteValue).match(/\((\d+)\)/);
            transporteValue = match && match[1] ? match[1] : String(transporteValue);
          }

          const detallesConSubtotales = cotizacionCargada.detalles?.map((detalle) => {
            const precioLista = (detalle as any).precioLista || detalle.precioUnitario || 0;
            const porcDesc = (detalle as any).porcDesc || 0;
            const subtotal = detalle.subtotal || 0;
            return { ...detalle, precioUnitario: precioLista, precioLista, descuento: porcDesc, porcDesc, subtotal };
          }) || [];

          const importeTotalCalculado = detallesConSubtotales.reduce((sum, d) => sum + (d.subtotal || 0), 0);
          const fechaEmisionDate = cotizacionCargada.fechaEmision ? (cotizacionCargada.fechaEmision instanceof Date ? cotizacionCargada.fechaEmision : new Date(cotizacionCargada.fechaEmision)) : new Date();
          const vencimComprobante = mapearVencimComprobanteCargado(cotizacionCargada, fechaEmisionDate);
          const horaEmisionDate = cotizacionCargada.horaEmision ? (cotizacionCargada.horaEmision instanceof Date ? cotizacionCargada.horaEmision : new Date(cotizacionCargada.horaEmision)) : new Date();

          const nroVendedorCargado = Number(
            (cotizacionCargada as any).nroVendedor ?? (cotizacionCargada as any).NroVendedor ?? 0,
          );

          setCotizacionVenta({
            ...cotizacionCargada,
            fechaEmision: fechaEmisionDate,
            fechaVencim: vencimComprobante.fechaVencim,
            vtoComprob: vencimComprobante.vtoComprob,
            horaEmision: horaEmisionDate,
            ordenCompra: (cotizacionCargada as any).ordenDeCompra || (cotizacionCargada as any).ordenCompra || '',
            transporte: transporteValue,
            ubicacion: ubicacionFormateada,
            formaDePago: (cotizacionCargada as any).formaDePago || '',
            nroSuc: (cotizacionCargada as any).nroSuc || DEFAULT_NRO_SUC,
            nroVendedor: Number.isFinite(nroVendedorCargado) && nroVendedorCargado > 0 ? nroVendedorCargado : 0,
            cuit: (cotizacionCargada as any).cuit || '',
            tipoIVA: (cotizacionCargada as any).tipoIVA || (cotizacionCargada as any).tipoIva || '',
            dni: (cotizacionCargada as any).dni || '',
            idCompania: (cotizacionCargada as any).idCompania || '',
            detalles: detallesConSubtotales,
            importeTotal: cotizacionCargada.importeTotal && cotizacionCargada.importeTotal > 0 ? cotizacionCargada.importeTotal : round2(importeTotalCalculado),
          } as CotizacionVentaUIExt);

          setPosCliente({
            nroCliente: Number(cotizacionCargada.nroCliente || 0),
            nombre: cotizacionCargada.nombre || '',
            cuit: (cotizacionCargada as any).cuit || '',
            dni: (cotizacionCargada as any).dni || '',
            tipoIva: (cotizacionCargada as any).tipoIVA || (cotizacionCargada as any).tipoIva || '',
            nroVendedor: Number.isFinite(nroVendedorCargado) && nroVendedorCargado > 0 ? nroVendedorCargado : 0,
          });
          setLineasPOS(
            detallesConSubtotales.map((d, idx) => ({
              id: `${cotizacionCargada.nroFormulario || 'cot'}-${idx + 1}`,
              codigoArticulo: d.codigoArticulo || '',
              descripcionArticulo: d.descripcionArticulo || '',
              cantidad: Number(d.cantidad || 0),
              precioUnitario: Number(d.precioUnitario || d.precioLista || 0),
              descuento: Number(d.descuento || d.porcDesc || 0),
              subtotal: Number(d.subtotal || 0),
              um: d.um || '',
              notas: d.notas,
            })),
          );
        } catch (err: unknown) {
          const message = getApiErrorMessage(err, 'Error al cargar la cotizacion');
          setError(message);
          enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
          setTimeout(() => navigate('/ventas/cotizaciones'), 2000);
        } finally {
          setLoadingCotizacion(false);
        }
      };
      cargarCotizacion();
    }
  }, [cotizacionId, navigate, enqueueSnackbar]);

  useEffect(() => {
    const cargarSucursales = async () => {
      setLoadingSucursales(true);
      try {
        const res = await api.get('/api/SucursalesVenta');
        const data: SucursalVenta[] = Array.isArray(res.data)
          ? res.data
          : res.data?.items ?? [];
        setSucursales(data);
      } catch {
        setSucursales([]);
      } finally {
        setLoadingSucursales(false);
      }
    };
    void cargarSucursales();
  }, []);

  // Cotización nueva: última sucursal usada o la primera del catálogo
  useEffect(() => {
    if (cotizacionId || loadingCotizacion) return;
    if (sucursales.length === 0) return;
    setCotizacionVenta((prev) => {
      if (Number(prev.nroSuc) > 0) return prev;
      const stored = Number(localStorage.getItem('nroSuc') || '0');
      const elegida = sucursales.find((s) => s.nroSuc === stored) ?? sucursales[0];
      if (!elegida) return prev;
      return { ...prev, nroSuc: elegida.nroSuc };
    });
  }, [sucursales, cotizacionId, loadingCotizacion]);

  const handleClienteSelect = (cliente: any) => {
    const posCli = cliente as POSCliente;
    setPosCliente(posCli);
    const transporteSugerido = obtenerTransporteSugerido(cliente);
    const formaPagoSugerida = obtenerFormaPagoSugerida(cliente);
    const vtoComprobCliente = resolveVtoComprobCliente(cliente);
    setCotizacionVenta((prev: CotizacionVentaUIExt) => ({
      ...prev,
      nroCliente: cliente.nroCliente ?? cliente.codigo ?? 0,
      nombre: cliente.nombre,
      cuit: cliente.cuit || '',
      dni: (cliente as any).dni || '',
      tipoIVA: cliente.tipoIva || '',
      vtoComprob: esCotizacionPersistente ? prev.vtoComprob : vtoComprobCliente,
      fechaVencim: esCotizacionPersistente
        ? (prev.fechaVencim ?? calcularFechaVencimDesdeVtoComprob(prev.fechaEmision ?? new Date(), prev.vtoComprob))
        : calcularFechaVencimDesdeVtoComprob(prev.fechaEmision ?? new Date(), vtoComprobCliente),
      nroVendedor:
        esCotizacionPersistente || isReadOnly
          ? prev.nroVendedor
          : Number((cliente as any).vendedor) > 0
            ? Number((cliente as any).vendedor)
            : 0,
      formaDePago: formaPagoSugerida,
      transporte: transporteSugerido,
      porcDesc: normalizarPorcentajeBonificacion(cliente.porcDesc ?? (cliente as any)?.porc_Desc ?? (cliente as any)?.porcdesc),
      porcDesc2: normalizarPorcentajeBonificacion(cliente.porcDesc2 ?? (cliente as any)?.porc_Desc2 ?? (cliente as any)?.porcdesc2),
      porcDesc3: normalizarPorcentajeBonificacion(cliente.porcDesc3 ?? (cliente as any)?.porc_Desc3 ?? (cliente as any)?.porcdesc3),
    }));
  };

  const handleAgregarProductoPOS = (item: SuggestionItem) => {
    setLineasPOS((prev) => [
      ...prev,
      {
        id: genId(),
        codigoArticulo: item.codigoArticulo,
        descripcionArticulo: item.descripcion,
        cantidad: 1,
        precioUnitario: item.precioUnitario,
        descuento: 0,
        subtotal: item.precioUnitario,
        um: item.um,
        stockDisponible: item.stockDisponible,
      },
    ]);
  };

  const handleLineaChange = (id: string, field: keyof POSLinea, value: string | number) => {
    setLineasPOS((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const updated = { ...l, [field]: value };
        updated.subtotal = calcularSubtotal(updated);
        return updated;
      }),
    );
  };

  const handleLineaFilled = (id: string, data: Partial<POSLinea>) => {
    setLineasPOS((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const updated = { ...l, ...data };
        updated.subtotal = calcularSubtotal(updated);
        return updated;
      }),
    );
  };

  const handleEliminarLinea = (id: string) => {
    setLineasPOS((prev) => prev.filter((l) => l.id !== id));
  };

  const handleDuplicarLinea = (id: string) => {
    setLineasPOS((prev) => {
      const idx = prev.findIndex((l) => l.id === id);
      if (idx === -1) return prev;
      const copia: POSLinea = { ...prev[idx], id: genId() };
      const next = [...prev];
      next.splice(idx + 1, 0, copia);
      return next;
    });
  };

  const handleAgregarLineaVacia = () => {
    setLineasPOS((prev) => [
      ...prev,
      { id: genId(), codigoArticulo: '', descripcionArticulo: '', cantidad: 1, precioUnitario: 0, descuento: 0, subtotal: 0, um: '' },
    ]);
  };

  useEffect(() => {
    const v = Number(cotizacionVenta.nroSuc);
    if (Number.isFinite(v) && v > 0) {
      try {
        localStorage.setItem('nroSuc', String(v));
        localStorage.setItem('sucursal', String(v));
      } catch {}
    }
  }, [cotizacionVenta.nroSuc]);

  /**
   * Solo en cotizaci?n NUEVA: hidratar defaults desde el maestro de clientes al ingresar nroCliente.
   * En editar/ver los datos de cabecera (incl. vendedor) vienen del GET; volver a llamar getCliente
   * provocaba condiciones de carrera y el vendedor del cliente pisaba el del comprobante.
   */
  useEffect(() => {
    if (esCotizacionPersistente || isReadOnly) {
      return undefined;
    }
    const value = cotizacionVenta.nroCliente;
    const handle = setTimeout(() => {
      const nro = parseInt((value ?? 0).toString());
      if (!isNaN(nro) && nro > 0) {
        (async () => {
          try {
            const c = await clienteService.getCliente(nro, OPTIONS_CLIENTE_OPERATIVO);
            const transporteSugerido = obtenerTransporteSugerido(c);
            const formaPagoSugerida = obtenerFormaPagoSugerida(c);
            const vtoComprobCliente = resolveVtoComprobCliente(c);
            setCotizacionVenta((prev) => {
              const newCalc = calcularFechaVencimDesdeVtoComprob(
                prev.fechaEmision ?? new Date(),
                vtoComprobCliente,
              );
              const oldCalc = calcularFechaVencimDesdeVtoComprob(
                prev.fechaEmision ?? new Date(),
                prev.vtoComprob ?? null,
              );
              const mantenerVencimManual = !sameCalendarDay(
                prev.fechaVencim ?? prev.fechaEmision,
                oldCalc,
              );
              return {
                ...prev,
                nroCliente: c.nroCliente,
                nombre: c.nombre,
                cuit: c.cuit || '',
                dni: (c as any).dni || '',
                tipoIVA: c.tipoIva || '',
                vtoComprob: vtoComprobCliente,
                fechaVencim: mantenerVencimManual ? (prev.fechaVencim ?? newCalc) : newCalc,
                nroVendedor:
                  Number((c as any).vendedor) > 0 ? Number((c as any).vendedor) : 0,
                formaDePago: formaPagoSugerida,
                transporte: transporteSugerido,
                porcDesc: normalizarPorcentajeBonificacion(
                  c.porcDesc ?? (c as any)?.porc_Desc ?? (c as any)?.porcdesc,
                ),
                porcDesc2: normalizarPorcentajeBonificacion(
                  c.porcDesc2 ?? (c as any)?.porc_Desc2 ?? (c as any)?.porcdesc2,
                ),
                porcDesc3: normalizarPorcentajeBonificacion(
                  c.porcDesc3 ?? (c as any)?.porc_Desc3 ?? (c as any)?.porcdesc3,
                ),
              } as CotizacionVentaUIExt;
            });
          } catch {
            /* ignore */
          }
        })();
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [cotizacionVenta.nroCliente, esCotizacionPersistente, isReadOnly]);

  useEffect(() => {
    const detalles = lineasPOS.map((l, idx): CotizacionVentaDetalle => ({
      nroIdformulario: cotizacionVenta.nroFormulario,
      nroItem: idx + 1,
      codigoArticulo: l.codigoArticulo,
      descripcionArticulo: l.descripcionArticulo,
      cantidad: l.cantidad,
      precioUnitario: l.precioUnitario,
      precioLista: l.precioUnitario,
      descuento: l.descuento,
      porcDesc: l.descuento,
      subtotal: l.subtotal,
      notas: l.notas,
      um: l.um,
    }));
    const importeTotal = detalles.reduce((acc, d) => acc + (d.subtotal || 0), 0);
    setCotizacionVenta((prev) => ({ ...prev, detalles, importeTotal: round2(importeTotal) }));
  }, [lineasPOS, cotizacionVenta.nroFormulario]);

  const subtotalNetoPOS = lineasPOS.reduce((acc, l) => acc + l.subtotal, 0);
  const importeDesc1POS = (subtotalNetoPOS * (cotizacionVenta.porcDesc || 0)) / 100;
  const baseDesc2POS = subtotalNetoPOS - importeDesc1POS;
  const importeDesc2POS = (baseDesc2POS * (cotizacionVenta.porcDesc2 || 0)) / 100;
  const baseDesc3POS = baseDesc2POS - importeDesc2POS;
  const importeDesc3POS = (baseDesc3POS * (cotizacionVenta.porcDesc3 || 0)) / 100;
  const subtotalConDescPOS = baseDesc3POS - importeDesc3POS;
  const ivaPOS = Math.round(subtotalConDescPOS * 0.21 * 100) / 100;
  const totalPOS = subtotalConDescPOS + ivaPOS;

  const handlePorcDescChangePOS = (field: 'porcDesc' | 'porcDesc2' | 'porcDesc3', v: number) => {
    setCotizacionVenta((prev) => ({ ...prev, [field]: Number.isFinite(v) ? v : 0 }));
  };

  const handleSave = async () => {
    if (isReadOnly) {
      enqueueSnackbar('No se puede guardar una cotizacion en modo solo lectura', { variant: 'warning' });
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const erroresValidacion: string[] = [];
      if (!cotizacionVenta.nroCliente || cotizacionVenta.nroCliente <= 0) erroresValidacion.push('Debe seleccionar un cliente');
      if (!cotizacionVenta.fechaEmision) erroresValidacion.push('Debe ingresar la Fecha de Emision');
      const fechaVencimError = validarFechasFacturaVenta(
        cotizacionVenta.fechaEmision,
        cotizacionVenta.fechaVencim ?? cotizacionVenta.fechaEmision,
        calcularFechaVencimDesdeVtoComprob(
          cotizacionVenta.fechaEmision,
          cotizacionVenta.vtoComprob,
        ),
      );
      if (fechaVencimError) erroresValidacion.push(fechaVencimError);
      if (!cotizacionVenta.nroSuc || cotizacionVenta.nroSuc <= 0) {
        erroresValidacion.push('Debe seleccionar una Sucursal / Punto de Venta');
      }
      const ubicacion = (cotizacionVenta as CotizacionVentaUI).ubicacion;
      if (!ubicacion || ubicacion.trim() === '') erroresValidacion.push('Debe seleccionar una Ubicacion');
      const formaDePago = (cotizacionVenta as CotizacionVentaUI).formaDePago;
      if (!formaDePago || formaDePago.trim() === '') erroresValidacion.push('Debe seleccionar una Forma de Pago');
      if (!cotizacionVenta.nroSuc || cotizacionVenta.nroSuc <= 0) {
        erroresValidacion.push('Debe seleccionar una Sucursal / Punto de Venta');
      }
      if (!cotizacionVenta.detalles || cotizacionVenta.detalles.length === 0) {
        erroresValidacion.push('Debe agregar al menos un producto a la cotizacion');
      } else {
        cotizacionVenta.detalles.forEach((detalle, index) => {
          if (!detalle.cantidad || detalle.cantidad <= 0) {
            erroresValidacion.push(`El producto "${detalle.descripcionArticulo || detalle.codigoArticulo}" (item ${index + 1}) debe tener una cantidad mayor a 0`);
          }
          if (!detalle.codigoArticulo) {
            erroresValidacion.push(`El item ${index + 1} no tiene c?digo de art?culo`);
          }
        });
      }

      if (erroresValidacion.length > 0) {
        erroresValidacion.forEach((e) => enqueueSnackbar(e, { variant: 'error', autoHideDuration: 5000 }));
        setError(erroresValidacion.join('\n'));
        setLoading(false);
        return;
      }

      const payload = {
        ...cotizacionVenta,
        nroSuc: cotizacionVenta.nroSuc,
        ordenCompra: cotizacionVenta.ordenCompra || '',
        transporte: cotizacionVenta.transporte || '',
        ubicacion: cotizacionVenta.ubicacion || '',
        formaDePago: cotizacionVenta.formaDePago || '',
      } as any;

      let response: CotizacionVenta;
      let nroCotizacion: number | null = null;

      if (isEditMode && cotizacionId) {
        const id = parseInt(cotizacionId, 10);
        await cotizacionVentaService.updateCotizacionVenta(id, payload);
        nroCotizacion = id;
        enqueueSnackbar('Cotizacion actualizada exitosamente', { variant: 'success', autoHideDuration: 3000 });
        setTimeout(() => navigate('/ventas/cotizaciones'), 1500);
        setLoading(false);
        return;
      } else {
        response = await cotizacionVentaService.createCotizacionVenta(payload);
        if (response?.nroFormulario) nroCotizacion = response.nroFormulario;
        else if ((response as any)?.formulario?.nroFormulario) nroCotizacion = (response as any).formulario.nroFormulario;
      }

      if (nroCotizacion && !isEditMode) {
        setNroCotizacionCreada(nroCotizacion);
        setSuccessDialogOpen(true);
        setCotizacionVenta({
          nroFormulario: 0,
          nroTipoFormulario: '10',
          tipoCliente: 'C',
          fechaEmision: new Date(),
          fechaVencim: new Date(),
          horaEmision: new Date(),
          nroCliente: 0,
          nombre: '',
          nroVendedor: 0,
          nroLista: '',
          codigoMoneda: 'ARS',
          importeTotal: 0,
          asentado: 'N',
          descripcion: '',
          usuario: '',
          ordenCompra: '',
          transporte: '',
          ubicacion: '',
          formaDePago: '',
          nroSuc: NRO_SUC_COTIZACION_NUEVA,
          cuit: '',
          tipoIVA: '',
          idCompania: '',
          detalles: [],
          porcDesc: 0,
          porcDesc2: 0,
          porcDesc3: 0,
        });
        setPosCliente(null);
        setLineasPOS([]);
      } else {
        enqueueSnackbar('Cotizacion guardada exitosamente', { variant: 'success' });
        setCotizacionVenta({
          nroFormulario: 0,
          nroTipoFormulario: '10',
          tipoCliente: 'C',
          fechaEmision: new Date(),
          fechaVencim: new Date(),
          horaEmision: new Date(),
          nroCliente: 0,
          nombre: '',
          nroVendedor: 0,
          nroLista: '',
          codigoMoneda: 'ARS',
          importeTotal: 0,
          asentado: 'N',
          descripcion: '',
          usuario: '',
          ordenCompra: '',
          transporte: '',
          ubicacion: '',
          formaDePago: '',
          nroSuc: NRO_SUC_COTIZACION_NUEVA,
          cuit: '',
          tipoIVA: '',
          idCompania: '',
          detalles: [],
          porcDesc: 0,
          porcDesc2: 0,
          porcDesc3: 0,
        });
        setPosCliente(null);
        setLineasPOS([]);
      }
    } catch (err: any) {
      const data = err?.response?.data;
      const status = err?.response?.status;
      let errorMessage = 'Error al guardar la cotizacion';
      if (err?.message && (err.message.includes('Se encontraron') || err.message.includes('obligatorio'))) {
        errorMessage = err.message;
        const partes = err.message.split('\n');
        const errores = partes.slice(1).map((e: string) => e.replace(/^\d+\.\s*/, '').trim()).filter((e: string) => e);
        errores.forEach((e: string) => { if (e.trim()) enqueueSnackbar(e.trim(), { variant: 'error', autoHideDuration: 6000 }); });
      } else if (data?.errors && typeof data.errors === 'object') {
        const msgs: string[] = [];
        for (const k of Object.keys(data.errors)) {
          const val = data.errors[k];
          if (Array.isArray(val)) msgs.push(`${k}: ${val.join(' - ')}`);
          else if (val) msgs.push(`${k}: ${String(val)}`);
        }
        if (msgs.length > 0) {
          errorMessage = msgs.join('\n');
          msgs.forEach((m: string) => enqueueSnackbar(m, { variant: 'error', autoHideDuration: 6000 }));
        }
      } else if (status === 400 || status === 500) {
        errorMessage = getApiErrorMessage(err, errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 8000 });
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessContinuar = () => {
    setSuccessDialogOpen(false);
    setNroCotizacionCreada(null);
    navigate('/ventas/cotizaciones');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: { xs: 1, sm: 2 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
        mb={2.5}
        flexWrap="wrap"
        useFlexGap
      >
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/ventas/cotizaciones'))}
            size="small"
            sx={{ textTransform: 'none' }}
          >
            Cotizaciones
          </Button>
          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
          <Typography variant="h5" fontWeight={700} color="primary.main">
            {isReadOnly
              ? `Ver Cotizacion #${cotizacionId}`
              : isEditMode
                ? `Editar Cotizacion #${cotizacionId}`
                : 'Nueva Cotizacion POS'}
          </Typography>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {loadingCotizacion ? (
        <Box
          sx={{
            minHeight: '40vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Cargando cotización…
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={8}>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="overline" color="text.secondary" display="block" mb={0.75}>
                  Cliente
                </Typography>
                <FacturaPOSClientePanel
                  cliente={posCliente}
                  onClienteSelect={(cliente) => {
                    if (!isReadOnly) handleClienteSelect(cliente);
                  }}
                  readOnly={isReadOnly}
                  formaDePago={cotizacionVenta.formaDePago || ''}
                  ordenCompra={cotizacionVenta.ordenCompra || ''}
                />
              </Box>
              {!isReadOnly && (
                <Box>
                  <Typography variant="overline" color="text.secondary" display="block" mb={0.75}>
                    Artículos
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <FacturaPOSSearchBar
                        nroLista={obtenerCodigoListaCliente(posCliente)}
                        disabled={!posCliente}
                        onAgregarProducto={handleAgregarProductoPOS}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
              <Box>
                {lineasPOS.length > 0 && (
                  <Stack direction="row" justifyContent="flex-end" mb={0.75}>
                    <Chip
                      label={`${lineasPOS.length} renglon${lineasPOS.length > 1 ? 'es' : ''}`}
                      size="small"
                    />
                  </Stack>
                )}
                <FacturaPOSLineasGrid
                  lineas={lineasPOS}
                  nroLista={obtenerCodigoListaCliente(posCliente)}
                  readOnly={isReadOnly}
                  onLineaChange={handleLineaChange}
                  onLineaFilled={handleLineaFilled}
                  onEliminarLinea={handleEliminarLinea}
                  onDuplicarLinea={handleDuplicarLinea}
                  onAgregarLineaVacia={handleAgregarLineaVacia}
                />
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
              <FacturaPOSTotalesPanel
                subtotalNeto={subtotalNetoPOS}
                porcDesc={cotizacionVenta.porcDesc || 0}
                porcDesc2={cotizacionVenta.porcDesc2 || 0}
                porcDesc3={cotizacionVenta.porcDesc3 || 0}
                onPorcDescChange={handlePorcDescChangePOS}
                impuestos={ivaPOS > 0 ? [{ tasa: 21, monto: ivaPOS }] : []}
                montoIVA={ivaPOS}
                total={totalPOS}
                observaciones={cotizacionVenta.descripcion ?? ''}
                onObservacionesChange={(v) =>
                  setCotizacionVenta((prev) => ({ ...prev, descripcion: v }))
                }
                cliente={posCliente}
                fechaEmision={toLocalDateStringYMD(
                  cotizacionVenta.fechaEmision instanceof Date
                    ? cotizacionVenta.fechaEmision
                    : new Date(cotizacionVenta.fechaEmision as string | number),
                )}
                onFechaEmisionChange={(ymd) =>
                  setCotizacionVenta((prev) => {
                    const fechaEmision = parseLocalDate(ymd);
                    const nuevoSugerido = calcularFechaVencimDesdeVtoComprob(
                      fechaEmision,
                      prev.vtoComprob,
                    );
                    const sugeridoAnterior = calcularFechaVencimDesdeVtoComprob(
                      prev.fechaEmision ?? new Date(),
                      prev.vtoComprob,
                    );
                    const mantenerManual = !sameCalendarDay(
                      prev.fechaVencim ?? prev.fechaEmision,
                      sugeridoAnterior,
                    );
                    return {
                      ...prev,
                      fechaEmision,
                      fechaVencim: mantenerManual ? (prev.fechaVencim ?? nuevoSugerido) : nuevoSugerido,
                    };
                  })
                }
                fechaVencim={toLocalDateInputValue(cotizacionVenta.fechaVencim ?? cotizacionVenta.fechaEmision)}
                fechaVencimMin={toLocalDateInputValue(
                  calcularFechaVencimDesdeVtoComprob(
                    cotizacionVenta.fechaEmision,
                    cotizacionVenta.vtoComprob,
                  ),
                )}
                fechaVencimHelperText={
                  cotizacionVenta.vtoComprob != null
                    ? 'Puede editarse, pero no ser anterior a la fecha sugerida'
                    : 'Si no hay plazo comercial, vence en la fecha de emisión'
                }
                onFechaVencimChange={(ymd) =>
                  setCotizacionVenta((prev) => ({ ...prev, fechaVencim: parseLocalDate(ymd) }))
                }
                sucursales={sucursales}
                nroSuc={Number(cotizacionVenta.nroSuc) || 0}
                onNroSucChange={(n) =>
                  setCotizacionVenta((prev) => ({ ...prev, nroSuc: n }))
                }
                loadingSucursales={loadingSucursales}
                idCompania={cotizacionVenta.idCompania ?? ''}
                onIdCompaniaChange={(id) =>
                  setCotizacionVenta((prev) => ({ ...prev, idCompania: id }))
                }
                nroVendedor={Number(cotizacionVenta.nroVendedor) || 0}
                nombreVendedorFallback={posCliente?.vendedorInfo?.nombreVendedor ?? undefined}
                onNroVendedorChange={(nro, nombre) => {
                  setCotizacionVenta((prev) => ({ ...prev, nroVendedor: nro ?? 0 }));
                  setPosCliente((prev) => {
                    if (!prev) return prev;
                    if (!nro) return { ...prev, nroVendedor: undefined, vendedorInfo: undefined };
                    return {
                      ...prev,
                      nroVendedor: nro,
                      vendedorInfo: { nroVendedor: nro, nombreVendedor: nombre },
                    };
                  });
                }}
                codigoLista={
                  obtenerCodigoListaCliente(posCliente) || String(cotizacionVenta.nroLista ?? '').trim()
                }
                onCodigoListaChange={(codigo, descripcion) => {
                  const c = String(codigo ?? '').trim();
                  setPosCliente((prev) => {
                    if (!prev) return prev;
                    if (!c) return { ...prev, nroLista: undefined, listaPrecio: undefined };
                    return {
                      ...prev,
                      nroLista: c,
                      listaPrecio: {
                        codigoLista: c,
                        descripcion: descripcion ?? prev.listaPrecio?.descripcion ?? null,
                      },
                    };
                  });
                  setCotizacionVenta((prev) => ({ ...prev, nroLista: c }));
                }}
                formaDePago={cotizacionVenta.formaDePago || ''}
                onFormaDePagoChange={(v) => setCotizacionVenta((prev) => ({ ...prev, formaDePago: v }))}
                ordenCompra={cotizacionVenta.ordenCompra || ''}
                onOrdenCompraChange={(v) => setCotizacionVenta((prev) => ({ ...prev, ordenCompra: v }))}
                transporte={cotizacionVenta.transporte || ''}
                onTransporteChange={(v) => setCotizacionVenta((prev) => ({ ...prev, transporte: v }))}
                ubicacion={cotizacionVenta.ubicacion || ''}
                onUbicacionChange={(v) => setCotizacionVenta((prev) => ({ ...prev, ubicacion: v }))}
                saving={loading}
                onGuardar={() => handleSave()}
                cantLineas={lineasPOS.length}
                readOnly={isReadOnly}
                hideDraftButton
                saveLabel={isEditMode ? 'Guardar Cotización' : 'Crear Cotización'}
                emisionNombre={posCliente?.nombre ?? cotizacionVenta.nombre ?? ''}
                onEmisionNombreChange={(v) => {
                  setPosCliente((p) => (p ? { ...p, nombre: v, razonSocial: v } : p));
                  setCotizacionVenta((prev) => ({ ...prev, nombre: v }));
                }}
                emisionCuit={posCliente?.cuit ?? cotizacionVenta.cuit ?? ''}
                onEmisionCuitChange={(v) => {
                  setPosCliente((p) => (p ? { ...p, cuit: v } : p));
                  setCotizacionVenta((prev) => ({ ...prev, cuit: v }));
                }}
                emisionTipoIva={posCliente?.tipoIva ?? cotizacionVenta.tipoIVA ?? ''}
                onEmisionTipoIvaChange={(v) => {
                  setPosCliente((p) => (p ? { ...p, tipoIva: v } : p));
                  setCotizacionVenta((prev) => ({ ...prev, tipoIVA: v }));
                }}
              />
            </Box>
          </Grid>
        </Grid>
      )}

      <Dialog
        open={successDialogOpen}
        onClose={(_, reason) => {
          if (reason === 'backdropClick') return;
          handleSuccessContinuar();
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1.5, pt: 3, px: 3 }}>Cotización creada</DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 2, px: 3 }}>
          <Typography variant="body1" color="text.secondary">
            La cotización se creó correctamente. Número de cotización: {nroCotizacionCreada}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            onClick={handleSuccessContinuar}
            variant="contained"
            color="primary"
            sx={{ textTransform: 'none' }}
          >
            Continuar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CotizacionesVenta;
